import { ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import { RunRow } from '@apps/web/features/teach_phoebe/ui/run_row';
import { type TrainingRunGroup } from '@apps/web/lib/training_run_utils';

/**
 * Renders real scheduler groups as collapsible sections of RunRows; runs Phoebe
 * did on its own (no scheduler) render flat, with no group header. Shared by the
 * Unreviewed, Reviewed, and All tabs so the layout stays identical across them.
 */
export function RunGroupList({
    groups,
    organizationId,
    onFeedbackSaved,
    onReviewingChange,
}: {
    readonly groups: ReadonlyArray<TrainingRunGroup>;
    readonly organizationId: string | null;
    readonly onFeedbackSaved: (
        runId: string,
        label: 'good' | 'bad' | null,
    ) => void;
    readonly onReviewingChange: (runId: string, isReviewing: boolean) => void;
}) {
    // Groups start collapsed so the tab reads as a scannable list of
    // schedulers; reviewers expand one person at a time.
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
        () => new Set(),
    );
    const [autoExpandedOwnGroupKey, setAutoExpandedOwnGroupKey] = useState<
        string | null
    >(null);

    const toggleGroup = (key: string) => {
        setExpandedKeys((current) => {
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    useEffect(() => {
        const ownGroupKey = groups.find((group) => group.isOwn)?.key ?? null;
        if (!ownGroupKey || ownGroupKey === autoExpandedOwnGroupKey) {
            return;
        }
        setExpandedKeys((current) => new Set(current).add(ownGroupKey));
        setAutoExpandedOwnGroupKey(ownGroupKey);
    }, [groups, autoExpandedOwnGroupKey]);

    return (
        <div>
            {groups.map((group) => {
                const runsId = `run-group-${group.key}`;
                // Runs Phoebe did on its own (voice, text, auto) have no
                // scheduler to attribute to, so they render as a flat list —
                // the row's type line already says what it is. Only real
                // scheduler groups get a collapsible person header.
                if (group.isPhoebe) {
                    return (
                        <ul
                            key={group.key}
                            className="border-b last:border-b-0"
                        >
                            {group.runs.map((run, index) => (
                                <RunRow
                                    key={run.id}
                                    run={run}
                                    isLast={index === group.runs.length - 1}
                                    organizationId={organizationId}
                                    onFeedbackSaved={onFeedbackSaved}
                                    onReviewingChange={onReviewingChange}
                                />
                            ))}
                        </ul>
                    );
                }
                const isExpanded = expandedKeys.has(group.key);
                return (
                    <div key={group.key} className="border-b last:border-b-0">
                        <button
                            type="button"
                            aria-expanded={isExpanded}
                            aria-controls={runsId}
                            onClick={() => toggleGroup(group.key)}
                            className="flex w-full cursor-pointer items-center gap-2 py-2.5 text-left"
                        >
                            {isExpanded ? (
                                <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
                            ) : (
                                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                            )}
                            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                                {group.label}
                            </span>
                            {group.isOwn && (
                                <span className="bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 text-[11px] font-medium">
                                    You
                                </span>
                            )}
                            <span className="text-muted-foreground text-xs font-medium">
                                {group.runs.length}
                            </span>
                        </button>
                        {isExpanded && (
                            <ul id={runsId} className="border-t">
                                {group.runs.map((run, index) => (
                                    <RunRow
                                        key={run.id}
                                        run={run}
                                        isLast={index === group.runs.length - 1}
                                        organizationId={organizationId}
                                        onFeedbackSaved={onFeedbackSaved}
                                        onReviewingChange={onReviewingChange}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
